/**
 * Owner: Masa Bayu
 * File: use-shell-route.ts
 * Date:Time: 2026-09-22 05:09
 * Description: History shell routing — R-01 soft redirects /sell·/profile · hash migrate.
 */

import { useEffect, useState } from 'react';
import {
  canonicalizeQxreadShellPath,
  isQxreadShellRoute,
} from '@qx/qxread-core';

function normalizeShellRaw(raw: string): string {
  const trimmed = raw.replace(/^#/, '');
  if (trimmed.length === 0) {
    return '/';
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/** Split pathname from `?query` for route guards and Search PW-D. */
export function splitShellLocation(raw: string): {
  readonly path: string;
  readonly search: string;
} {
  const normalized = normalizeShellRaw(raw);
  const qIndex = normalized.indexOf('?');
  if (qIndex < 0) {
    return { path: normalized, search: '' };
  }
  return {
    path: normalized.slice(0, qIndex) || '/',
    search: normalized.slice(qIndex + 1),
  };
}

export function resolveShellPath(raw: string): {
  readonly path: string;
  readonly search: string;
  readonly allowed: boolean;
  readonly redirectedFrom: string | null;
} {
  const { path: incoming, search } = splitShellLocation(raw);
  const path = canonicalizeQxreadShellPath(incoming);
  return {
    path,
    search,
    allowed: isQxreadShellRoute(path),
    redirectedFrom: path !== incoming ? incoming : null,
  };
}

export function readSearchParam(search: string, key: string): string {
  if (search.length === 0) {
    return '';
  }
  const params = new URLSearchParams(search);
  return params.get(key) ?? '';
}

/** Current shell location — pathname preferred; hash only if still present (tests/legacy). */
export function readBrowserShellRaw(): string {
  const hash = window.location.hash;
  if (hash.length > 1) {
    return hash;
  }
  const path =
    window.location.pathname && window.location.pathname.length > 0
      ? window.location.pathname
      : '/';
  const q = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search;
  return q.length > 0 ? `${path}?${q}` : path;
}

export function shellHref(path: string, search = ''): string {
  const resolved = resolveShellPath(path);
  return search.length > 0 || resolved.search.length > 0
    ? `${resolved.path}?${search.length > 0 ? search : resolved.search}`
    : resolved.path;
}

function writeCanonicalUrl(path: string, search: string, mode: 'replace' | 'push'): void {
  const url = search.length > 0 ? `${path}?${search}` : path;
  if (mode === 'replace') {
    window.history.replaceState(null, '', url);
  } else {
    window.history.pushState(null, '', url);
  }
}

/** One-time migrate `#/discover` → `/discover` for bookmarks. */
export function migrateLegacyHashRoute(): void {
  const hash = window.location.hash;
  if (hash.length <= 1) {
    return;
  }
  const next = normalizeShellRaw(hash);
  window.history.replaceState(null, '', next);
}

export function useShellRoute(): {
  readonly path: string;
  readonly search: string;
  readonly allowed: boolean;
  readonly navigate: (next: string) => void;
} {
  const initial = resolveShellPath(readBrowserShellRaw());
  const [path, setPath] = useState(initial.path);
  const [search, setSearch] = useState(initial.search);
  const [allowed, setAllowed] = useState(initial.allowed);

  useEffect(() => {
    const sync = () => {
      const hash = window.location.hash;
      if (hash.length > 1) {
        window.history.replaceState(null, '', normalizeShellRaw(hash));
      }
      const resolved = resolveShellPath(readBrowserShellRaw());
      if (resolved.redirectedFrom !== null) {
        writeCanonicalUrl(resolved.path, resolved.search, 'replace');
      }
      setPath(resolved.path);
      setSearch(resolved.search);
      setAllowed(resolved.allowed);
    };
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    sync();
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
    };
  }, []);

  const navigate = (next: string) => {
    const resolved = resolveShellPath(next);
    writeCanonicalUrl(
      resolved.path,
      resolved.search,
      resolved.redirectedFrom !== null ? 'replace' : 'push',
    );
    setPath(resolved.path);
    setSearch(resolved.search);
    setAllowed(resolved.allowed);
  };

  return { path, search, allowed, navigate };
}
